import { motion, useScroll, useTransform } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useChangeBackgroundColor } from '#app/hooks/use-change-background-color.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { cn } from '#app/utils/misc.tsx'

interface Property {
	title: string
	image: string
	svg?: string
}

interface ShowcaseProps {
	className?: string
	sectionTitle: string
	showcaseItems: Property[]
}

export default function PropertiesShowcase({
	sectionTitle,
	showcaseItems,
	className,
}: ShowcaseProps) {
	const container = useRef<HTMLDivElement>(null)
	const ref = useChangeBackgroundColor(extendedTheme.colors.brand.dusk.DEFAULT)
	const [index, setIndex] = useState(0)

	const { scrollYProgress } = useScroll({
		target: container,
		offset: ['start start', 'end end'],
	})

	// Map vertical scroll progress to horizontal X movement
	const x = useTransform(scrollYProgress, [0, 1], ['0%', '-88%'])
	const activeIndex = useTransform(
		scrollYProgress,
		[0, 1],
		[0, showcaseItems.length - 1],
	)

	useEffect(() => {
		activeIndex.on('change', (latest) => {
			setIndex(Math.ceil(latest))
		})
	}, [activeIndex])

	// TODO: Change this to LandingSection with color change implemented

	return (
		<div ref={container} className={cn('relative', className)}>
			<div
				ref={ref}
				className={
					'sticky top-0 h-screen max-w-[100vw] overflow-x-hidden text-white lg:max-w-[calc(100vw-100px)]'
				}
			>
				<div className={'grid h-full w-full grid-rows-5'}>
					<h2
						className={
							'mx-[10vw] mt-[10vh] w-full text-lg lowercase tracking-[0.2em] md:text-xl'
						}
					>
						/ {sectionTitle} /
					</h2>
					<motion.div
						style={{
							x,
						}}
						className={
							'row-start-3 mx-[10vw] flex items-center justify-center gap-sm self-start'
						}
					>
						{showcaseItems.map((item, i) => (
							<PropertyCard isActive={index === i} key={item.title} {...item} />
						))}
					</motion.div>
				</div>
			</div>

			<div style={{ height: `${showcaseItems.length * 20}vh` }} />
		</div>
	)
}

function PropertyCard({ isActive, ...item }: { isActive: boolean } & Property) {
	const ref = useRef<HTMLHeadingElement>(null)

	const textHeight =
		(ref.current ? -ref.current.clientHeight : -30) - (item.svg ? 80 : 24)

	return (
		<div
			className={
				'relative flex h-[min(40vh,350px)] w-[clamp(300px,40vw,500px)] flex-col'
			}
		>
			<motion.div
				animate={{
					y: isActive ? textHeight : 0,
					opacity: isActive ? 1 : 0.4,
					scale: isActive ? 1.02 : 0.98,
				}}
				transition={{
					duration: 0.8,
					ease: [0.25, 0.46, 0.45, 0.94],
				}}
				className={
					'z-20 flex h-full min-h-full w-full flex-col items-start gap-md bg-brand-dusk bg-opacity-100'
				}
			>
				{item.svg && (
					<img src={item.svg} alt={item.title} className={'z-20 h-[50px]'} />
				)}
				<img
					src={item.image}
					alt={item.title}
					className={'z-20 h-full w-full object-cover'}
				/>
			</motion.div>

			<motion.h3
				animate={{
					opacity: isActive ? 1 : 0,
				}}
				ref={ref}
				className={
					'absolute bottom-0 z-0 font-serif text-h3 !font-light md:text-h2'
				}
			>
				{item.title}
			</motion.h3>
		</div>
	)
}
